import { type FC, useLayoutEffect, useRef } from 'react';
import { Assert } from '@team/assert';
import { Dom } from '@team/dom';
import { ReactHooks } from '@team/react-hooks';
import { AppearanceType, getAppearanceClassNameByType } from '../appearance';
import type { Props } from './types';
import { AppRootContextDefinition } from './interfaces';
import { Mode } from './enums';
import { Context } from './context';
import {
  CUSTOM_PROPERTY_CONTENT_INSET_PREFIX,
  CUSTOM_PROPERTY_INSET_PREFIX,
} from './model';
import { FALLBACK_SAFE_AREA_INSETS } from './const';
import { useContentSafeAreaInsets, useSafeAreaInsetsSetup } from './hooks';
import css from './style.module.css';

export const Entry: FC<Props> = ({
  mode = Mode.FULL,
  appearance = AppearanceType.LIGHT,
  safeAreaInsets: inputSafeAreaInsets,
  contentSafeAreaInsets: inputContentSafeAreaInsets,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<Nullable<HTMLElement>>(null);

  const safeAreaInsets = ReactHooks.useObjectMemo(inputSafeAreaInsets);
  const contentSafeAreaInsets = useContentSafeAreaInsets(
    inputContentSafeAreaInsets
  );

  useLayoutEffect(() => {
    if (Assert.isNull(rootRef.current)) {
      return;
    }

    const parentElement = rootRef.current.parentElement;
    const bodyElement = Dom.getDocumentBody(rootRef.current);
    const htmlElement = bodyElement.ownerDocument.documentElement;

    scrollableRef.current = parentElement;

    htmlElement.classList.add(css.html);

    /**
     * 💬 Если приложение запущено на мобильном платформе,
     * то включаем stiсky-поведение
     */
    if (mode === Mode.STICKY) {
      htmlElement.classList.add(css.sticky);
    }

    bodyElement.classList.add(css.body);

    if (Assert.isNotNull(parentElement)) {
      parentElement.classList.add(css.root);
    }

    return () => {
      scrollableRef.current = null;

      htmlElement.classList.remove(css.html);

      if (mode === Mode.STICKY) {
        htmlElement.classList.remove(css.stiсky);
      }

      bodyElement.classList.remove(css.body);

      if (Assert.isNotNull(parentElement)) {
        parentElement.classList.remove(css.root);
      }
    };
  }, [mode]);

  useLayoutEffect(() => {
    if (Assert.isNull(rootRef.current)) {
      return;
    }

    const bodyElement = Dom.getDocumentBody(rootRef.current);
    const htmlElement = bodyElement.ownerDocument.documentElement;

    const appearanceClassName = getAppearanceClassNameByType(appearance);

    htmlElement.classList.add(appearanceClassName);

    return () => {
      htmlElement.classList.remove(appearanceClassName);
    };
  }, [appearance]);

  useSafeAreaInsetsSetup({
    insets: safeAreaInsets,
    customPropertyPrefix: CUSTOM_PROPERTY_INSET_PREFIX,
    targetRef: rootRef,
  });

  useSafeAreaInsetsSetup({
    insets: contentSafeAreaInsets.value,
    customPropertyPrefix: CUSTOM_PROPERTY_CONTENT_INSET_PREFIX,
    targetRef: rootRef,
  });

  const contextValue = ReactHooks.useObjectMemo<AppRootContextDefinition>({
    appRoot: rootRef,
    scrollable: scrollableRef,
    mode,
    appearance,
    safeAreaInsets: safeAreaInsets ?? FALLBACK_SAFE_AREA_INSETS,
    contentSafeAreaInsets:
      contentSafeAreaInsets.value ?? FALLBACK_SAFE_AREA_INSETS,
    registerContentSafeAreaInsets: contentSafeAreaInsets.register,
    unregisterContentSafeAreaInsets: contentSafeAreaInsets.unregister,
  });

  return (
    <div className={css.inner} ref={rootRef}>
      <Context.Provider value={contextValue}>{children}</Context.Provider>
    </div>
  );
};
